import { useState } from 'react';
import { Download } from 'lucide-react';
import { EXPORT_PERMS } from '@mlm/shared';
import { Button, toast } from '@mlm/ui';
import { ExportButtons as SharedExportButtons } from '@mlm/ui/components/export-buttons';
import { useHasPerm } from '../lib/auth-store';
import type { ReportResult } from '../lib/api';
import { reportToExportTable, type ReportExportMeta } from '../lib/report-export';

/**
 * The admin's binding of the shared `ExportButtons`, plus the CSV button the
 * cashier doesn't have.
 *
 * Excel and PDF come from `@mlm/ui` (subpath-only, so jsPDF and the zip writer
 * stay in async chunks) and are built from the payload already on screen; CSV
 * is still rendered by the server, so every tenant's existing CSV files are
 * unchanged. All three are gated per format on the global, ADVISORY
 * `export.csv` / `export.excel` / `export.pdf` codes — the data reached the
 * browser under the page's own read perm, so these hide affordances rather
 * than guard anything.
 *
 * `useHasPerm` is passed to the shared component as a module-level function on
 * purpose: it is invoked as a hook there, so its identity must be stable.
 */
const PERMS = { excel: EXPORT_PERMS.excel, pdf: EXPORT_PERMS.pdf };

export type ReportExportButtonsProps = {
  /** The payload on screen. Excel/PDF are built from it verbatim. */
  result?: ReportResult;
  /** Title / subtitle / filename for the generated files. */
  meta: ReportExportMeta;
  /** Fetch + save the server-rendered CSV (usually `downloadReport(…,'csv',…)`). */
  onCsv: () => Promise<void>;
  disabled?: boolean;
  /** Caveat shown beside the buttons, e.g. a row cap. */
  note?: string;
  /**
   * Override the perm codes. The Consignment surfaces pass
   * `consignment.export` for all three — grandfathered, so a tenant that
   * already narrowed consignment exports is not re-opened here.
   */
  perms?: { csv: string; excel: string; pdf: string };
};

export function ReportExportButtons({
  result,
  meta,
  onCsv,
  disabled,
  note,
  perms,
}: ReportExportButtonsProps) {
  const canCsv = useHasPerm(perms?.csv ?? EXPORT_PERMS.csv);
  const [busy, setBusy] = useState(false);

  const runCsv = async () => {
    setBusy(true);
    try {
      await onCsv();
    } catch (err) {
      console.error(err);
      toast.error('Export failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const blocked = disabled || !result?.rows.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canCsv && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          title={note}
          disabled={blocked || busy}
          onClick={() => void runCsv()}
        >
          <Download className="h-4 w-4" />
          {busy ? 'Exporting…' : 'Export CSV'}
        </Button>
      )}
      <SharedExportButtons
        // A THUNK: the table (and its generation timestamp) is snapshotted at
        // click time, not at render time.
        table={() => (result ? reportToExportTable(result, meta) : null)}
        disabled={blocked}
        note={note}
        perms={perms ? { excel: perms.excel, pdf: perms.pdf } : PERMS}
        useHasPerm={useHasPerm}
      />
    </div>
  );
}
